import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Loader2, BellRing, ChevronLeft, ChevronRight } from 'lucide-react';

const TRIGGER_META = {
  stage_change: { icon: '🔄', label: 'Stage Change' },
  new_event: { icon: '📅', label: 'Meeting / Call' },
  new_comment: { icon: '💬', label: 'Comment' },
  new_edit: { icon: '✏️', label: 'Edit' },
  test: { icon: '🔔', label: 'Test' },
};

const STATUS_STYLE = {
  sent: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
  failed: 'bg-red-500/10 text-red-400 border-red-500/30',
  expired: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
  no_subscription: 'bg-secondary text-muted-foreground border-border',
};

const STATUS_LABEL = {
  sent: 'Delivered', failed: 'Failed', expired: 'Expired', no_subscription: 'No device',
};

const PER_PAGE = 50;

function timeAgo(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.getNotificationHistory(page, PER_PAGE)
      .then(res => { setRows(res.data.results || []); setTotal(res.data.total || 0); })
      .catch(() => { setRows([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [page]);

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  return (
    <div className="max-w-5xl space-y-6" data-testid="notifications-page">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="font-heading text-lg text-foreground flex items-center gap-2">
            <BellRing className="w-4 h-4 text-blue-400" /> Notification History
          </h2>
          <p className="text-sm text-muted-foreground mt-1">Audit of push alerts the system sent — who received what, and when.</p>
        </div>
        <span className="text-xs text-muted-foreground font-mono" data-testid="notifications-total">{total} events</span>
      </div>

      <div className="bg-card border border-border rounded-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16 text-sm text-muted-foreground" data-testid="notifications-empty">
            No notifications have been sent yet.
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-border uppercase tracking-wider">
                <th className="py-2.5 px-3 w-[15%]">When</th>
                <th className="py-2.5 px-3 w-[16%]">Trigger</th>
                <th className="py-2.5 px-3">Deal / Message</th>
                <th className="py-2.5 px-3 w-[18%]">Recipient</th>
                <th className="py-2.5 px-3 w-[12%]">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const meta = TRIGGER_META[r.trigger] || { icon: '•', label: r.trigger || '—' };
                return (
                  <tr key={r.id || i} className={`border-b border-border/50 ${i % 2 ? 'bg-secondary/30' : ''}`} data-testid={`notification-row-${i}`}>
                    <td className="py-2 px-3 text-muted-foreground whitespace-nowrap" title={r.created_at}>{timeAgo(r.created_at)}</td>
                    <td className="py-2 px-3"><span className="text-foreground">{meta.icon} {meta.label}</span></td>
                    <td className="py-2 px-3">
                      {r.project_id ? (
                        <button onClick={() => navigate(`/projects/${r.project_id}`)} className="text-blue-400 hover:text-blue-300 font-medium">
                          {r.project_name || 'Untitled deal'}
                        </button>
                      ) : (
                        <span className="text-foreground font-medium">{r.title}</span>
                      )}
                      <div className="text-muted-foreground/70 truncate max-w-md">{r.body}</div>
                    </td>
                    <td className="py-2 px-3 text-foreground">{r.recipient_name || '—'}</td>
                    <td className="py-2 px-3">
                      <span className={`inline-block px-1.5 py-0.5 rounded-sm border text-[10px] font-mono ${STATUS_STYLE[r.status] || STATUS_STYLE.no_subscription}`}>
                        {STATUS_LABEL[r.status] || r.status}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Page {page} of {totalPages}</span>
          <div className="flex items-center gap-2">
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="notifications-prev"
              className="flex items-center gap-1 px-2 py-1 rounded-sm border border-border disabled:opacity-40 hover:text-foreground">
              <ChevronLeft className="w-3 h-3" /> Prev
            </button>
            <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="notifications-next"
              className="flex items-center gap-1 px-2 py-1 rounded-sm border border-border disabled:opacity-40 hover:text-foreground">
              Next <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
